import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { downloadReport } from "@/lib/api";
import { inr, fmtDate, billNo } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { SearchSelect } from "@/components/SearchSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { FileDown, FileSpreadsheet, Columns3, Scale } from "lucide-react";

const ALL_COLS = [
  { key: "purchase_no", label: "Bill No" },
  { key: "date", label: "Date" },
  { key: "supplier_name", label: "Supplier" },
  { key: "supplier_category", label: "Category" },
  { key: "total", label: "Total", money: true },
  { key: "amount_paid", label: "Paid", money: true },
  { key: "balance", label: "Balance", money: true },
  { key: "status", label: "Status" },
];

export default function BalanceReport() {
  const [supplierId, setSupplierId] = useState("all");
  const [category, setCategory] = useState("all");
  const [cols, setCols] = useState(ALL_COLS.map((c) => c.key));

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers-min"],
    queryFn: async () => (await api.get("/suppliers")).data,
  });
  const { data: cats = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/suppliers/categories")).data,
  });
  const { data: purchases = [] } = useQuery({
    queryKey: ["balance-purchases", supplierId],
    queryFn: async () =>
      (await api.get("/purchases", { params: { supplier_id: supplierId === "all" ? undefined : supplierId } })).data,
  });

  const rows = useMemo(() => {
    let r = purchases.filter((p) => Number(p.balance) > 0);
    if (category !== "all") r = r.filter((p) => p.supplier_category === category);
    const openingRows = suppliers
      .filter((s) => Number(s.opening_balance) > 0)
      .filter((s) => supplierId === "all" || s.id === supplierId)
      .filter((s) => category === "all" || s.category === category)
      .map((s) => ({
        id: `opening-${s.id}`, purchase_no: "OPENING BAL", bill_number: "OPENING BAL", date: "", supplier_name: s.name,
        supplier_category: s.category || "", total: Number(s.opening_balance), amount_paid: 0,
        balance: Number(s.opening_balance), status: "opening",
      }));
    return [...openingRows, ...r].sort((a, b) => (a.supplier_name || "").localeCompare(b.supplier_name || "") || (a.date || "").localeCompare(b.date || ""));
  }, [purchases, category, suppliers, supplierId]);

  const totalBal = rows.reduce((s, p) => s + Number(p.balance || 0), 0);
  const totalDue = rows.reduce((s, p) => s + Number(p.total || 0), 0);
  const activeCols = ALL_COLS.filter((c) => cols.includes(c.key));

  const toggleCol = (k) =>
    setCols((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));

  const exportParams = (fmt) => ({
    fmt, category,
    supplier_id: supplierId === "all" ? undefined : supplierId,
  });

  return (
    <div>
      <PageTitle title="Balance Report" subtitle="Outstanding supplier balances — flexible columns">
        <Button variant="outline" onClick={() => downloadReport("/reports/balance/export", exportParams("pdf"), "balance")} data-testid="balance-pdf">
          <FileDown className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" onClick={() => downloadReport("/reports/balance/export", exportParams("excel"), "balance")} data-testid="balance-excel">
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </Button>
      </PageTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card className="p-4"><div className="text-sm text-muted-foreground">Outstanding Bills</div><div className="font-display text-2xl font-bold">{rows.length}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Total Balance</div><div className="font-display text-2xl font-bold text-destructive data-num">{inr(totalBal)}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Total Billed</div><div className="font-display text-2xl font-bold data-num">{inr(totalDue)}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Paid So Far</div><div className="font-display text-2xl font-bold text-success data-num">{inr(totalDue - totalBal)}</div></Card>
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Supplier</label>
            <div className="mt-1">
              <SearchSelect
                value={supplierId}
                onChange={setSupplierId}
                options={[{ value: "all", label: "All Suppliers" }, ...suppliers.map((s) => ({ value: s.id, label: `${s.supplier_code} — ${s.name}` }))]}
                searchPlaceholder="Type supplier name or code…"
                testid="balance-supplier"
              />
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Category</label>
            <div className="mt-1">
              <SearchSelect
                value={category}
                onChange={setCategory}
                options={[{ value: "all", label: "All Categories" }, ...cats.map((c) => ({ value: c, label: c }))]}
                searchPlaceholder="Type category…"
                testid="balance-category"
              />
            </div>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="column-picker"><Columns3 className="h-4 w-4" /> Columns</Button>
            </PopoverTrigger>
            <PopoverContent className="w-56">
              <div className="text-sm font-semibold mb-2">Visible columns</div>
              <div className="space-y-2">
                {ALL_COLS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={cols.includes(c.key)} onCheckedChange={() => toggleCol(c.key)} data-testid={`col-${c.key}`} />
                    {c.label}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              {activeCols.map((c) => <TableHead key={c.key} className={c.money ? "text-right" : ""}>{c.label}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={activeCols.length} className="text-center py-10 text-muted-foreground"><Scale className="h-6 w-6 mx-auto mb-2 opacity-40" />No outstanding balances.</TableCell></TableRow>
              ) : rows.map((p) => (
                <TableRow key={p.id} data-testid="balance-row">
                  {activeCols.map((c) => (
                    <TableCell key={c.key} className={c.money ? "text-right data-num font-medium" : ""}>
                      {c.key === "date" ? (p.date ? fmtDate(p.date) : "—")
                        : c.key === "purchase_no" ? billNo(p.bill_number)
                        : c.money ? inr(p[c.key])
                        : c.key === "status" ? <Badge variant={p.status === "partial" ? "default" : p.status === "opening" ? "outline" : "secondary"}>{p.status}</Badge>
                        : p[c.key] ?? "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
